import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { portalApi, sessionHeaders, getPortalSwitch } from './portal/portalApi';
import { getSessionToken } from './staffSessionStorage';
import { mediaFileUrl } from './mediaFileUrl';

// What opening a Parcel or Package label's code shows (/b/<token>, issues #300, #313, #445,
// vis_order_fulfilment.md "Labels"). The code is a plain web link, so
// any phone camera opens it, signed in or not. Sits outside the staff sign-in
// (see AuthGate.js); who's looking only changes what's shown:
//   - staff signed in to Optim on this browser, who can read the order's
//     fulfilment, see a read-only inquiry (issue #306): the order and who
//     it's for, where the package is up to, and its shipment, with links
//     to the order and the shipment. Nothing here changes anything;
//     working the package is done on Pack and Ship Order;
//   - the Customer, signed in to the portal on this browser, sees the
//     package's contents and its shipment, for their own orders only;
//   - anyone else sees what's on the label and who sent it, so a package
//     gone astray can be reported.
// The backend (controllers/boxScanController.js) decides; this page only
// passes on whichever sessions this browser holds.

const PAGE = { minHeight: '100vh', background: '#f4f6f9', padding: '24px 16px', boxSizing: 'border-box', fontFamily: 'inherit' };
const CARD = { maxWidth: 480, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 20 };
const HEADING = { fontSize: 12, fontWeight: 700, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.4, margin: '18px 0 6px' };
const MUTED = { fontSize: 14, color: '#5a6b85' };

function tokenFromPath() {
  const match = window.location.pathname.match(/^\/b\/([^/]+)\/?$/);
  return match ? decodeURIComponent(match[1]) : '';
}

// Where the package is up to, for staff.
const BOX_STATES = {
  open: { label: 'Open', detail: 'Still being packed.', color: '#9a6700', background: '#fff4e0' },
  closed: { label: 'Closed', detail: 'Packed and waiting to ship.', color: '#1a4971', background: '#eef4fb' },
  shipped: { label: 'Shipped', detail: null, color: '#1e7e34', background: '#e6f4ea' },
};

function shipmentLink(order, shipment) {
  const params = new URLSearchParams({ order_type: order.order_type, order_id: String(order.id), shipment: String(shipment.id) });
  return `/warehouse-shipments?${params}`;
}

// Marks an item with no weight recorded (it leaves the package's expected
// weight unknown).
const NO_WEIGHT_MARK = { color: '#b45309', fontWeight: 700, marginLeft: 2 };

const formatWeight = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const LINK = { color: '#1976d2', fontSize: 14, fontWeight: 600, textDecoration: 'none' };

function ShipmentDetails({ shipment }) {
  return (
    <div style={{ fontSize: 14, lineHeight: 1.6 }}>
      <div>Shipped {shipment.shipped_date || '—'}{shipment.carrier ? ` with ${shipment.carrier}` : ''}</div>
      {shipment.tracking_number && <div>Tracking number: {shipment.tracking_number}</div>}
      {shipment.tracking_url && (
        <a href={shipment.tracking_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...LINK }}>
          Track parcel <ExternalLink size={13} />
        </a>
      )}
    </div>
  );
}

// Who sent it, so a parcel or package gone astray can be reported.
function SentBy({ sender }) {
  return (
    <>
      <div style={HEADING}>Sent by</div>
      <div style={{ fontSize: 14, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600 }}>{sender.name}</div>
        {sender.address_lines.map((l) => <div key={l}>{l}</div>)}
        {sender.phone && <div><a href={`tel:${sender.phone.replace(/\s+/g, '')}`} style={{ color: '#1976d2' }}>{sender.phone}</a></div>}
        {sender.email && <div><a href={`mailto:${sender.email}`} style={{ color: '#1976d2' }}>{sender.email}</a></div>}
        {sender.website && <div>{sender.website}</div>}
      </div>
      <div style={{ ...MUTED, marginTop: 12, fontSize: 13 }}>If this has reached you by mistake, please let us know.</div>
    </>
  );
}

function SenderHeading({ sender }) {
  return sender.logo_id
    ? <img src={mediaFileUrl(sender.logo_id)} alt={sender.name} style={{ maxHeight: 56, maxWidth: 220, objectFit: 'contain', display: 'block', marginBottom: 12 }} />
    : <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{sender.name}</div>;
}

async function fetchScan(token, headers) {
  const res = await portalApi.get(`/api/box/${encodeURIComponent(token)}`, { headers });
  return res.data.data;
}

function BoxScanPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = tokenFromPath();
    let cancelled = false;
    (async () => {
      try {
        const staffToken = getSessionToken();
        const first = await fetchScan(token, staffToken ? { Authorization: `Bearer ${staffToken}` } : {});
        // A Login Switcher tab is an Admin seeing what that Customer sees,
        // so it asks as the Customer even with a staff sign-in.
        if (first.portal_name && getPortalSwitch(first.portal_name)) {
          const asCustomer = await fetchScan(token, sessionHeaders(first.portal_name));
          if (!cancelled) setData(asCustomer);
          return;
        }
        if (first.viewer === 'staff') {
          if (!cancelled) setData(first);
          return;
        }
        // Signed in to this business's portal on this browser: ask again
        // as that Customer.
        const portalHeaders = first.portal_name ? sessionHeaders(first.portal_name) : {};
        const result = Object.keys(portalHeaders).length ? await fetchScan(token, portalHeaders) : first;
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err.response
            ? (err.response.data?.message || 'This package label isn\'t recognised.')
            : 'Could not reach the server. It may be starting up, so try again in a moment.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <div style={PAGE}><div style={CARD}><div style={MUTED}>{error}</div></div></div>;
  }
  if (!data) {
    return <div style={PAGE}><div style={CARD}><div style={MUTED}>Loading...</div></div></div>;
  }

  const { label, sender, shipment } = data;
  // A label scanned by anyone not signed in shows only who sent it
  // (issue #445).
  if (data.viewer === 'public') {
    return (
      <div style={PAGE}>
        <div style={CARD}>
          <SenderHeading sender={sender} />
          <SentBy sender={sender} />
        </div>
      </div>
    );
  }
  const isCustomer = data.viewer === 'customer';
  const isStaff = data.viewer === 'staff';
  const boxState = isStaff ? BOX_STATES[data.package.state] : null;
  // Items whose weight isn't recorded, marked in the contents list.
  const missingWeight = new Set(label.weight?.missing || []);

  return (
    <div style={PAGE}>
      <div style={CARD}>
        <SenderHeading sender={sender} />

        {/* Bottom-aligned, so the package's label sits level with the order
            number rather than the order type above it. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: '#5a6b85' }}>{label.type_label}</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{label.display_number}</div>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, textAlign: 'right' }}>{label.package_label}</div>
        </div>
        {isStaff && label.package_name && <div style={{ ...MUTED, textAlign: 'right', marginTop: 2 }}>{label.package_name}</div>}

        {isStaff && (
          <>
            <div style={{ fontSize: 14, marginTop: 4 }}>{data.order.party?.role}: <strong>{data.order.party?.name}</strong></div>
            {data.order.on_hold && (
              <div style={{ background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 8, padding: '8px 12px', marginTop: 10, fontSize: 13, color: '#7a5c00' }}>
                <strong>On Hold</strong>{data.order.on_hold_reason ? ` — ${data.order.on_hold_reason}` : ''}
              </div>
            )}
            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: boxState.background, color: boxState.color, fontSize: 14 }}>
              <strong>{boxState.label}</strong>
              {boxState.detail && ` — ${boxState.detail}`}
            </div>
          </>
        )}

        {label.weight && (() => {
          const complete = label.weight.value !== null && label.weight.value !== undefined;
          // Plain grey, so the package's status above stays what stands out; the
          // amber * marks any item missing a weight.
          return (
            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: 14, background: '#f6f8fa', color: '#222' }}>
              <div>
                Est weight <strong>{formatWeight(complete ? label.weight.value : label.weight.known)} {label.weight.unit}</strong>, + packaging
              </div>
              {!complete && <div style={{ fontSize: 13 }}><span style={NO_WEIGHT_MARK}>*</span> = no weight</div>}
            </div>
          );
        })()}

        <div style={HEADING}>{label.kind === 'parcel' ? 'In this parcel' : 'In this package'}</div>
        {label.contents.length === 0 && !(label.inside || []).length && <div style={MUTED}>Nothing recorded.</div>}
        {label.contents.map((c, i) => (
          <div key={`${c.sku}-${i}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid #eef1f5', fontSize: 14 }}>
            <span>
              <span style={{ fontWeight: 600 }}>{c.sku}</span>
              {missingWeight.has(c.sku) && <span style={NO_WEIGHT_MARK} title="No weight recorded">*</span>}
              {c.name && <span style={{ color: '#5a6b85' }}> {c.name}</span>}
            </span>
            <span style={{ whiteSpace: 'nowrap' }}>{Number(c.qty).toLocaleString(undefined, { maximumFractionDigits: 2 })}{c.uom ? ` ${c.uom}` : ''}</span>
          </div>
        ))}
        {/* The packages inside it, each with its own label. */}
        {(label.inside || []).map((name) => (
          <div key={name} style={{ padding: '6px 0', borderBottom: '1px solid #eef1f5', fontSize: 14, fontWeight: 600 }}>{name}</div>
        ))}

        {isCustomer && (
          <>
            <div style={HEADING}>Shipment</div>
            {shipment ? <ShipmentDetails shipment={shipment} /> : <div style={MUTED}>Not shipped yet.</div>}
            <div style={{ marginTop: 14 }}>
              <a href={`/portal/${data.portal_name}/orders/${data.order_id}`} style={{ color: '#1976d2', fontSize: 14, fontWeight: 600 }}>View the order</a>
            </div>
          </>
        )}

        {isStaff && (
          <>
            {shipment && (
              <>
                <div style={HEADING}>Shipment</div>
                <ShipmentDetails shipment={shipment} />
              </>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 18 }}>
              <a href={data.order.link} style={LINK}>View {label.type_label}</a>
              {shipment && <a href={shipmentLink(data.order, shipment)} style={LINK}>View Shipment</a>}
            </div>
          </>
        )}

        {!isCustomer && !isStaff && <SentBy sender={sender} />}
      </div>
    </div>
  );
}

export default BoxScanPage;
